import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigationType } from "react-router-dom";
import { useEffect, lazy, Suspense } from "react";
import Navbar from "@/components/Navbar";
import Hero from "@/components/Hero";
import Services from "@/components/Services";
import WhyChooseUs from "@/components/WhyChooseUs";
import TrustedBy from "@/components/TrustedBy";
import Gallery from "@/components/Gallery";
import Testimonials from "@/components/Testimonials";
import LatestBlog from "@/components/LatestBlog";
import NewsletterSignup from "@/components/NewsletterSignup";
import ContactForm from "@/components/ContactForm";
import Footer from "@/components/Footer";
import { SafeJsonLd, buildFaqSchema } from "@/utils/seo";
import HOMEPAGE_FAQS from "@/data/homepageFaqs";
import StickyCTA from "@/components/StickyCTA";
import TrustSignalStrip from "@/components/TrustSignalStrip";
import SocialProofToast from "@/components/SocialProofToast";
import AnnouncementBar from "@/components/AnnouncementBar";
import InstallPrompt from "@/components/InstallPrompt";
import { registerServiceWorker } from "@/utils/sw-register";
import { RouteErrorBoundary } from "@/components/RouteErrorBoundary";
import { updateRobots } from "@/utils/seo";
import { trackPage } from "@/utils/analyticsPrivacy";

// Lazy-loaded PWA pages — only shipped to the browser when the user visits
// /app, /self-help, or /emergency, so the main site bundle stays slim.
const SupportLayout = lazy(() => import("@/support/SupportLayout"));
const HomeScreen = lazy(() => import("@/support/pages/HomeScreen"));
const HelpCenter = lazy(() => import("@/support/pages/HelpCenter"));
const HelpGuide = lazy(() => import("@/support/pages/HelpGuide"));
const FixAssistant = lazy(() => import("@/support/pages/FixAssistant"));
const TechRequest = lazy(() => import("@/support/pages/TechRequest"));
const BookScreen = lazy(() => import("@/support/pages/BookScreen"));
const UrgentScreen = lazy(() => import("@/support/pages/UrgentScreen"));
const ContactScreen = lazy(() => import("@/support/pages/ContactScreen"));
const InstallScreen = lazy(() => import("@/support/pages/InstallScreen"));
const SupportAdmin = lazy(() => import("@/support/admin/SupportAdmin"));
const AccountScreen = lazy(() => import("@/support/pages/AccountScreen"));
const PropertyScreen = lazy(() => import("@/support/pages/PropertyScreen"));
const AuthCallback = lazy(() => import("@/support/components/AuthCallback"));

const SUPPORT_PATH = /^\/(app|help|book|urgent|staff|d|admin\/support)(\/|$)/;
const PublicDoc = lazy(() => import("@/support/pages/PublicDoc"));
const StaffApp = lazy(() => import("@/staff/StaffApp"));
const OLD_GUIDE_SLUGS = {
  "tv-wont-turn-on": "tv-no-signal",
  "tv-no-sound": "tv-no-sound-soundbar",
  "wifi-slow": "wifi-slow-or-dropping",
  "wifi-wrong-password": "device-wont-connect-to-wifi",
  "sonos-wont-play": "sonos-not-connecting",
  "smart-lights-wont-respond": "smart-home-device-offline",
  "camera-no-feed": "security-camera-offline",
  "printer-wont-print": "printer-not-printing",
  "streaming-buffering": "streaming-buffering",
};

function OldGuideRedirect() {
  const slug = useLocation().pathname.split("/").pop();
  return <Navigate to={OLD_GUIDE_SLUGS[slug] ? `/help/${OLD_GUIDE_SLUGS[slug]}` : "/help"} replace />;
}

// Register the service worker as soon as the module loads so the PWA is
// install-ready on first visit in production.
registerServiceWorker();
import TOP_REVIEWS from "@/data/reviews";

// Route-level code splitting — these screens are loaded on-demand so the
// homepage bundle stays small. Each lazy() import becomes its own JS chunk.
const GeoPage = lazy(() => import("@/components/GeoPage"));
const ServicePage = lazy(() => import("@/components/ServicePage"));
const ServiceLocationPage = lazy(() => import("@/components/ServiceLocationPage"));
const ServicesHub = lazy(() => import("@/components/ServicesHub"));
const LocationsHub = lazy(() => import("@/components/LocationsHub"));
const BlogList = lazy(() => import("@/components/BlogList"));
const BlogPost = lazy(() => import("@/components/BlogPost"));
const TvCalculator = lazy(() => import("@/components/TvCalculator"));
const DirectoryLinks = lazy(() => import("@/components/DirectoryLinks"));
const NewsletterAdmin = lazy(() => import("@/components/NewsletterAdmin"));
const OrdersAdmin = lazy(() => import("@/components/OrdersAdmin"));
const ShopSuccess = lazy(() => import("@/components/ShopPage").then((m) => ({ default: m.ShopSuccess })));
const BundleBuilder = lazy(() => import("@/components/BundleBuilder"));
const StoreHome = lazy(() => import("@/store/StoreHome"));
const StoreCategory = lazy(() => import("@/store/StoreHome").then((m) => ({ default: m.StoreCategory })));
const ProductPage = lazy(() => import("@/store/ProductPages").then((m) => ({ default: m.ProductPage })));
const BundlePage = lazy(() => import("@/store/ProductPages").then((m) => ({ default: m.BundlePage })));
const BundlesIndex = lazy(() => import("@/store/ProductPages").then((m) => ({ default: m.BundlesIndex })));
const ComparePage = lazy(() => import("@/store/ProductPages").then((m) => ({ default: m.ComparePage })));
const CartPage = lazy(() => import("@/store/CartPages").then((m) => ({ default: m.CartPage })));
const QuotePage = lazy(() => import("@/store/CartPages").then((m) => ({ default: m.QuotePage })));
const GuidesIndex = lazy(() => import("@/store/Guides").then((m) => ({ default: m.GuidesIndex })));
const GuidePage = lazy(() => import("@/store/Guides").then((m) => ({ default: m.GuidePage })));
const AdvisorPage = lazy(() => import("@/store/Advisor"));
const OrderPage = lazy(() => import("@/store/CartPages").then((m) => ({ default: m.OrderPage })));
const PortfolioDetail = lazy(() => import("@/components/PortfolioDetail"));
const PrivacyPolicy = lazy(() => import("@/components/PrivacyPolicy"));
const TermsOfService = lazy(() => import("@/components/TermsOfService"));
const ShippingPolicy = lazy(() => import("@/components/ShippingPolicy"));
const MoneyPage = lazy(() => import("@/components/MoneyPage"));
const OwnerProfile = lazy(() => import("@/components/OwnerProfile"));
const ManagedIT = lazy(() => import("@/components/ManagedIT"));
const MiamiServicePage = lazy(() => import("@/components/MiamiPage"));
const MiamiHub = lazy(() => import("@/components/MiamiPage").then((m) => ({ default: m.MiamiHub })));
const NotFound = lazy(() => import("@/components/NotFound"));

function RouteFallback() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center" aria-label="Loading">
      <div className="h-10 w-10 rounded-full border-2 border-white/10 border-t-blue-500 animate-spin" />
    </div>
  );
}

function ScrollRedirect({ section }) {
  return <Navigate to={`/#${section}`} replace />;
}

// New pages start at the top; #hash links wait for the target to render, then scroll to it
function ScrollManager() {
  const { pathname, hash, key } = useLocation();
  const navType = useNavigationType();
  useEffect(() => {
    const id = decodeURIComponent(hash.slice(1));
    if (!id || id.includes("=")) {
      if (navType !== "POP") window.scrollTo({ top: 0, left: 0, behavior: "instant" });
      return undefined;
    }
    let tries = 0;
    const timer = setInterval(() => {
      const el = document.getElementById(id);
      if (el || ++tries > 40) {
        clearInterval(timer);
        el?.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 75);
    return () => clearInterval(timer);
  }, [pathname, hash, key, navType]);
  return null;
}

// Fires a GA4 page_view event on every SPA route change
function GAPageTracker() {
  const location = useLocation();
  useEffect(() => {
    if (typeof window.gtag === "function") {
      window.gtag("event", "page_view", {
        page_path: location.pathname + location.search,
        page_location: window.location.href,
        page_title: document.title,
      });
    }
  }, [location]);
  return null;
}

function HomePage() {
  const reviewSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: "I.T. Help of SWFL",
    url: "https://techhelpswfl.com",
    telephone: "+1-239-834-4377",
    priceRange: "$$$",
    address: { "@type": "PostalAddress", addressRegion: "FL", addressCountry: "US" },
    aggregateRating: { "@type": "AggregateRating", ratingValue: "5.0", reviewCount: String(89), bestRating: "5" },
    review: TOP_REVIEWS.map((r) => ({
      "@type": "Review",
      author: { "@type": "Person", name: r.author },
      datePublished: r.date,
      reviewBody: r.text,
      reviewRating: { "@type": "Rating", ratingValue: r.rating, bestRating: "5" },
    })),
  };
  return (
    <main>
      <SafeJsonLd schema={buildFaqSchema(HOMEPAGE_FAQS)} />
      <SafeJsonLd schema={reviewSchema} />
      <Hero />
      <Services />
      <WhyChooseUs />
      <TrustedBy />
      <Gallery />
      <Testimonials />
      <LatestBlog />
      <section className="py-16 md:py-20 px-6 md:px-12">
        <NewsletterSignup variant="card" source="homepage" />
      </section>
      <ContactForm />
    </main>
  );
}

function AppShell() {
  const location = useLocation();
  const isSupport = SUPPORT_PATH.test(location.pathname);
  const isShop = /^\/shop(\/|$)/.test(location.pathname);
  useEffect(() => {
    document.body.dataset.checkoutQuiet = /^\/shop\/(cart|quote|order)(\/|$)/.test(location.pathname) ? "true" : "false";
    return () => { delete document.body.dataset.checkoutQuiet; };
  }, [location.pathname]);
  useEffect(() => {
    updateRobots(location.pathname);
    trackPage(location.pathname);
    document.querySelectorAll('script[data-prerender-schema]').forEach((script) => script.remove());
    if (!location.pathname.startsWith("/blog/")) document.querySelectorAll('meta[property^="article:"]').forEach((meta) => meta.remove());
  }, [location.pathname, location.search]);
  if (location.hash?.includes("session_id=")) {
    return <Suspense fallback={null}><AuthCallback /></Suspense>;
  }
  return (
      <div data-testid="app-root" className={isSupport ? "min-h-screen" : "min-h-screen bg-[#050A11] text-white"}>
        <a href="#route-content" data-testid="skip-to-content" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[60] focus:bg-blue-600 focus:text-white focus:px-4 focus:py-2 focus:rounded-lg">
          Skip to content
        </a>
        {!isSupport && !/^\/shop\/(cart|quote|order)(\/|$)/.test(location.pathname) && <AnnouncementBar />}
        {!isSupport && <Navbar />}
        {!isSupport && <TrustSignalStrip />}
        <GAPageTracker />
        <ScrollManager />
        <div id="route-content" tabIndex={-1} className="min-w-0 focus:outline-none"><RouteErrorBoundary key={location.pathname}><Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route element={<SupportLayout />}>
              <Route path="/app" element={<HomeScreen />} />
              <Route path="/app/fix" element={<FixAssistant />} />
              <Route path="/app/request" element={<TechRequest />} />
              <Route path="/app/contact" element={<ContactScreen />} />
              <Route path="/app/install" element={<InstallScreen />} />
              <Route path="/app/account" element={<AccountScreen />} />
              <Route path="/app/account/property/:id" element={<PropertyScreen />} />
              <Route path="/help" element={<HelpCenter />} />
              <Route path="/help/:slug" element={<HelpGuide />} />
              <Route path="/book" element={<BookScreen />} />
              <Route path="/urgent" element={<UrgentScreen />} />
            </Route>
            <Route path="/admin/support" element={<SupportAdmin />} />
            <Route path="/staff/*" element={<StaffApp />} />
            <Route path="/d/:token" element={<PublicDoc />} />
            <Route path="/self-help" element={<Navigate to="/help" replace />} />
            <Route path="/self-help/:slug" element={<OldGuideRedirect />} />
            <Route path="/emergency" element={<Navigate to="/urgent" replace />} />
            <Route path="/blog" element={<BlogList />} />
            <Route path="/blog/:blogSlug" element={<BlogPost />} />
            <Route path="/tools/tv-size-calculator" element={<TvCalculator />} />
            <Route path="/directory" element={<DirectoryLinks />} />
            <Route path="/admin/newsletter" element={<NewsletterAdmin />} />
            <Route path="/admin/orders" element={<OrdersAdmin />} />
            <Route path="/shop" element={<StoreHome />} />
            <Route path="/shop/c/:id" element={<StoreCategory />} />
            <Route path="/shop/brand/:brand" element={<StoreCategory />} />
            <Route path="/shop/p/:slug" element={<ProductPage key={location.pathname} />} />
            <Route path="/shop/bundles" element={<BundlesIndex />} />
            <Route path="/shop/bundles/:slug" element={<BundlePage key={location.pathname + location.search} />} />
            <Route path="/shop/compare" element={<ComparePage />} />
            <Route path="/shop/cart" element={<CartPage />} />
            <Route path="/shop/quote" element={<QuotePage />} />
            <Route path="/shop/order/:id" element={<OrderPage />} />
            <Route path="/shop/guides" element={<GuidesIndex />} />
            <Route path="/shop/advisor" element={<AdvisorPage />} />
            <Route path="/shop/guides/:slug" element={<GuidePage />} />
            <Route path="/shop/build" element={<BundleBuilder />} />
            <Route path="/shop/success" element={<ShopSuccess />} />
            <Route path="/services" element={<ServicesHub />} />
            <Route path="/services/sonos-installer-naples" element={<MoneyPage />} />
            <Route path="/services/managed-it" element={<ManagedIT />} />
            <Route path="/managed-it-services" element={<Navigate to="/services/business-it" replace />} />
            <Route path="/miami" element={<MiamiHub />} />
            <Route path="/miami/:miamiSlug" element={<MiamiServicePage />} />
            <Route path="/about/jason" element={<OwnerProfile />} />
            <Route path="/services/unifi-installer-naples" element={<MoneyPage />} />
            <Route path="/services/sonos-installer-fort-myers" element={<MoneyPage />} />
            <Route path="/services/unifi-installer-fort-myers" element={<MoneyPage />} />
            <Route path="/services/lutron-installer-naples" element={<MoneyPage />} />
            <Route path="/services/business-wifi-naples" element={<MoneyPage />} />
            <Route path="/services/luxury-smart-home-miami" element={<MoneyPage />} />
            <Route path="/services/sonos-installer-miami" element={<MoneyPage />} />
            <Route path="/services/home-theater-miami" element={<MoneyPage />} />
            <Route path="/services/tv-mounting-miami" element={<MoneyPage />} />
            <Route path="/services/:serviceSlug" element={<ServicePage />} />
            <Route path="/services/:serviceSlug/:geoSlug" element={<ServiceLocationPage />} />
            <Route path="/locations" element={<LocationsHub />} />
            <Route path="/portfolio/:slug" element={<PortfolioDetail />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/shipping-policy" element={<ShippingPolicy />} />
            <Route path="/terms" element={<Navigate to="/terms-of-service" replace />} />

            {/* Old Squarespace URL redirects */}
            <Route path="/home" element={<Navigate to="/" replace />} />
            <Route path="/index" element={<Navigate to="/" replace />} />
            <Route path="/about" element={<ScrollRedirect section="why-us" />} />
            <Route path="/about-it-help-of-swfl" element={<ScrollRedirect section="why-us" />} />
            <Route path="/contact" element={<ScrollRedirect section="contact" />} />
            <Route path="/contact-1" element={<ScrollRedirect section="contact" />} />
            <Route path="/professional-tv-mounting" element={<Navigate to="/services/tv-mounting" replace />} />
            <Route path="/tv-mounting" element={<Navigate to="/services/tv-mounting" replace />} />
            <Route path="/smart-home" element={<Navigate to="/services/smart-home" replace />} />
            <Route path="/smart-home-installation" element={<Navigate to="/services/smart-home" replace />} />
            <Route path="/it-support" element={<Navigate to="/services/it-support" replace />} />
            <Route path="/home-theater" element={<Navigate to="/services/home-theater" replace />} />
            <Route path="/home-theater-setup" element={<Navigate to="/services/home-theater" replace />} />
            <Route path="/network-setup" element={<Navigate to="/services/wifi-network" replace />} />
            <Route path="/wifi-setup" element={<Navigate to="/services/wifi-network" replace />} />
            <Route path="/digital-signage" element={<Navigate to="/services/business-it" replace />} />
            <Route path="/cybersecurity" element={<Navigate to="/services/business-it" replace />} />
            <Route path="/small-business-it" element={<Navigate to="/services/business-it" replace />} />
            <Route path="/gallery" element={<ScrollRedirect section="portfolio" />} />
            <Route path="/reviews" element={<ScrollRedirect section="reviews" />} />
            <Route path="/book-online" element={<Navigate to="/book" replace />} />

            <Route path="/:slug" element={<GeoPage />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense></RouteErrorBoundary></div>
        {!isSupport && (
          <>
            <Footer />
            {!isShop && <><StickyCTA /><SocialProofToast /><InstallPrompt /></>}
          </>
        )}
      </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppShell />
    </BrowserRouter>
  );
}

export default App;
